import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the NAATI vs court-registered translator guide. */
const TITLE = "Penerjemah NAATI vs penerjemah terdaftar pengadilan Thailand — mana yang diterima?";
const DESC =
  "Kapan instansi Australia mewajibkan terjemahan NAATI, kapan penerjemah terdaftar pengadilan atau Lawyers Council Thailand yang tepat, dan cara menghindari biaya ganda.";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Apa itu terjemahan NAATI?",
    answer:
      "Terjemahan yang dikerjakan penerjemah bersertifikat NAATI, badan akreditasi penerjemah di Australia. Instansi imigrasi dan pendidikan Australia umumnya meminta format ini.",
  },
  {
    question: "Kapan penerjemah terdaftar pengadilan Thailand dipakai?",
    answer:
      "Saat dokumen dipakai di dalam Thailand atau harus melewati pengesahan Departemen Konsuler, karena instansi Thailand mengakui penerjemah tersebut.",
  },
  {
    question: "Apakah keduanya bisa saling menggantikan?",
    answer:
      "Tidak selalu. Instansi Australia sering menolak terjemahan non-NAATI, sedangkan Departemen Konsuler Thailand memiliki daftar penerjemah yang diakui sendiri.",
  },
  {
    question: "Bagaimana agar tidak membayar dua kali?",
    answer:
      "Tanyakan lebih dulu kepada instansi penerima: apakah mereka meminta NAATI, terjemahan tersahkan, atau keduanya. Baru tentukan jalur penerjemahannya.",
  },
  {
    question: "Apakah terjemahan NAATI perlu pengesahan tambahan?",
    answer:
      "Untuk kebanyakan pengajuan di Australia tidak perlu. Namun bila dokumen juga dipakai di Thailand atau negara lain, lapisan pengesahan tetap diperlukan.",
  },
  {
    question: "Dokumen apa yang paling sering diminta?",
    answer:
      "Akta kelahiran, akta perkawinan, sertifikat perubahan nama, KK, surat keterangan catatan kepolisian, transkrip, dan dokumen perusahaan.",
  },
  {
    question: "Kenapa terjemahan ditolak?",
    answer:
      "Nama tidak sama dengan paspor, cap dan stempel tidak diterjemahkan, format sertifikasi penerjemah tidak lengkap, dan dokumen sumber yang sudah kedaluwarsa.",
  },
];

export const Route = createFileRoute("/id/blog/naati-vs-court-registered-translator")({
  component: IdNaatiGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "terjemahan NAATI Thailand, penerjemah tersertifikasi NAATI, penerjemah terdaftar pengadilan Thailand, terjemahan dokumen Thailand",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const CHOICE: Array<{ d: string; note: string }> = [
  { d: "Instansi Australia (visa, studi, profesi)", note: "Umumnya wajib terjemahan penerjemah bersertifikat NAATI" },
  { d: "Instansi Thailand", note: "Penerjemah yang diakui pengadilan atau Lawyers Council Thailand" },
  { d: "Pengesahan Departemen Konsuler", note: "Ikuti daftar penerjemah yang diakui Departemen Konsuler" },
  { d: "Kedutaan negara ketiga", note: "Tanyakan format terjemahan dan lapisan pengesahan yang diminta" },
  { d: "Dipakai di dua negara", note: "Siapkan dua set: satu NAATI, satu tersahkan untuk jalur Thailand" },
  { d: "Dokumen perusahaan", note: "Salinan baru dari DBD lalu terjemahan lengkap dengan cap dan stempel" },
];

const ORDER: string[] = [
  "Konfirmasi format terjemahan yang diminta instansi penerima secara tertulis",
  "Siapkan dokumen sumber versi terbaru dari instansi penerbit",
  "Pilih jalur penerjemah: NAATI atau penerjemah yang diakui di Thailand",
  "Notarisasi bila dokumen dibuat sendiri, misalnya pernyataan atau surat kuasa",
  "Pengesahan Departemen Konsuler bila jalur Thailand yang berlaku",
  "Periksa ulang ejaan nama, tanggal, dan kelengkapan cap sebelum diserahkan",
];

const MISTAKES: string[] = [
  "Memilih jalur penerjemah tanpa menanyakan instansi penerima",
  "Cap dan stempel dokumen tidak diterjemahkan",
  "Ejaan nama berbeda dari paspor",
  "Dokumen sumber sudah melewati batas waktu penerimaan",
  "Menerjemahkan lebih dulu lalu harus mengulang setelah pengesahan",
  "Jumlah set terjemahan kurang untuk pengajuan paralel",
];

function IdNaatiGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="id" />
      <p className="answer-summary speakable-summary">
        Pilihan penerjemah ditentukan instansi penerima, bukan jenis dokumennya. Instansi Australia umumnya mewajibkan
        penerjemah bersertifikat NAATI, sedangkan jalur Thailand — termasuk pengesahan Departemen Konsuler — mengakui
        penerjemah terdaftar pengadilan atau Lawyers Council. Tanyakan dulu, baru terjemahkan.
      </p>

      <h2>1. Pilih jalur sesuai tujuan</h2>
      <ul>
        {CHOICE.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan langkah</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Kesalahan yang membuat harus mengulang</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Rujukan dan layanan terkait</h2>
      <p className="text-sm">
        Rujukan:{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI (Australia)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/naati" className="text-primary underline">
            Layanan terjemahan NAATI
          </Link>
        </li>
        <li>
          <Link to="/id/sworn" className="text-primary underline">
            Penerjemah tersumpah dan terdaftar
          </Link>
        </li>
        <li>
          <Link to="/id/lgz" className="text-primary underline">
            Pengesahan dan legalisasi dokumen
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Ditinjau pada {PUBLISHED}. Persyaratan setiap instansi dapat berubah, jadi konfirmasi format terjemahan sebelum
        memulai. Jika Anda ingin satu pihak yang mengurus semuanya, tim kami dengan pengalaman lebih dari 15 tahun siap
        memberi saran jalur yang tepat dan mengerjakan seluruh prosesnya — hubungi kami lewat telepon, LINE, atau email.
      </p>
    </article>
  );
}
